// Lógica compartilhada de resolução/assinatura dos documentos de identidade
// (babás e famílias). Fonte única para admin, painel da babá e painel da
// família — evita a lógica duplicada que existia no DashboardAdmin.
//
// Regras:
//  - Tudo vive no bucket único `cadastros` (ver storage-bucket-convention).
//  - `documentos_url` (JSONB) guarda { frente, verso, criminal, residencia }
//    onde cada valor é uma string de path ou { path: "..." }.
//  - A "selfie" da babá/família É a foto do perfil (foto_url) — não há upload
//    de selfie separado no cadastro.
import { supabase } from "../../supabaseClient";

export const DOCS_BUCKET = "cadastros";

// Slots exibidos por tipo de usuário, na ordem de exibição.
// `source: "foto"` lê de usuario.foto_url; "docs" lê de documentos_url[key].
export const DOC_SLOTS = {
  baba: [
    { key: "foto", label: "Foto / Selfie", source: "foto" },
    { key: "frente", label: "RG — Frente", source: "docs" },
    { key: "verso", label: "RG — Verso", source: "docs" },
    { key: "criminal", label: "Antecedentes criminais", source: "docs" },
    { key: "residencia", label: "Comprovante de residência", source: "docs" },
  ],
  familia: [
    { key: "foto", label: "Foto / Selfie", source: "foto" },
    { key: "frente", label: "Documento — Frente", source: "docs" },
    { key: "verso", label: "Documento — Verso", source: "docs" },
    { key: "criminal", label: "Comprovante", source: "docs" },
  ],
};

// Normaliza um valor de path: remove prefixo de bucket / URL pública ou assinada.
export function getRelativePath(path, bucket = DOCS_BUCKET) {
  if (!path || typeof path !== "string") return null;
  let rel = path;
  const rx = new RegExp(`.*/storage/v1/object/(?:public|sign)/${bucket}/`);
  if (rel.match(rx)) rel = rel.replace(rx, "");
  if (rel.startsWith(`${bucket}/`)) rel = rel.substring(bucket.length + 1);
  if (rel.startsWith("/")) rel = rel.substring(1);
  if (rel === "" || rel === "undefined" || rel === "null") return null;
  return rel;
}

// Extrai o path bruto de documentos_url[key], lidando com os formatos
// heterogêneos do histórico: string JSON, objeto, string direta ou { path }.
export function resolveDocPath(documentosUrl, key) {
  let docs = documentosUrl;
  if (typeof docs === "string") {
    try {
      docs = JSON.parse(docs);
    } catch {
      return null;
    }
  }
  if (!docs || typeof docs !== "object") return null;
  const val = docs[key];
  if (!val) return null;
  return typeof val === "object" ? val.path || null : val;
}

// Monta a lista de slots resolvidos (com path relativo) para um usuário.
export function buildDocList(user, role = "baba") {
  const slots = DOC_SLOTS[role] || DOC_SLOTS.baba;
  return slots.map((slot) => {
    const raw =
      slot.source === "foto"
        ? user?.foto_url
        : resolveDocPath(user?.documentos_url, slot.key);
    return { ...slot, path: getRelativePath(raw) };
  });
}

// Gera uma URL assinada (1h) para um path relativo no bucket `cadastros`.
export async function signDocumentUrl(path) {
  const rel = getRelativePath(path);
  if (!rel) return null;
  const { data, error } = await supabase.storage
    .from(DOCS_BUCKET)
    .createSignedUrl(rel, 3600);
  if (error) return null;
  return data?.signedUrl ?? null;
}

export const isPdfPath = (path) => /\.pdf(\?|$)/i.test(path || "");
